/**
 * Bucket 5 §5.5.4 — Slice 0.3b: display-only No-Show Risk presence label.
 *
 * Pure helper extracted from `TechnicianColumn.computePresenceLabel()` so the
 * Awaiting → No-Show Risk transition can be unit-tested without standing up
 * the full React component, shop-time hook, supabase realtime subscriptions,
 * and date context.
 *
 * Doctrine (Bucket 5 §5.5.4):
 *   "If time clock is enabled and a scheduled technician fails to clock in by
 *   a shop-configured grace threshold: [...] The no-show grace threshold is a
 *   Shop Culture setting."
 *
 * Slice scope:
 *   - Display only. Returning 'No-Show Risk' does NOT mark the technician
 *     absent, change is_present, block timers, change OptiAssign / smart-
 *     assign eligibility, emit board issues, or alter pull-forward.
 *   - Callers must already have established that:
 *       * Time clock is enabled
 *       * The technician is scheduled today
 *       * Current shop-local time is within the shift window
 *       * The technician has no active clock entry
 *       * The technician has no `clock_out_reason` on today's last entry
 *     This helper only resolves the final two-way split between Awaiting and
 *     No-Show Risk based on the grace threshold.
 *
 * Default threshold (15 minutes) is owned by the caller — this helper does
 * NOT apply a default. Callers should pass `shop_culture.no_show_grace_
 * threshold_min ?? 15`.
 *
 * Boundary semantics:
 *   - `currentShopMinutes < shiftStartShopMinutes + thresholdMin` → 'Awaiting'
 *   - `currentShopMinutes >= shiftStartShopMinutes + thresholdMin` → 'No-Show Risk'
 *   - When `thresholdMin === 0`, the tech becomes 'No-Show Risk' immediately
 *     at shift start (per the Slice 0.3b spec).
 */

export type TimeClockUnclockedLabel = 'Awaiting' | 'No-Show Risk';

export interface TimeClockUnclockedLabelInput {
  /** Current shop-local time as minutes-from-midnight. */
  currentShopMinutes: number;
  /** Scheduled shift-start time as minutes-from-midnight in the shop timezone. */
  shiftStartShopMinutes: number;
  /** Grace minutes from `shop_culture.no_show_grace_threshold_min`. */
  thresholdMin: number;
}

/**
 * Decide between 'Awaiting' and 'No-Show Risk' for a scheduled, mid-shift,
 * unclocked technician under time-clock-enabled mode.
 */
export function getTimeClockUnclockedLabel(
  input: TimeClockUnclockedLabelInput,
): TimeClockUnclockedLabel {
  const { currentShopMinutes, shiftStartShopMinutes, thresholdMin } = input;
  const safeThreshold = Number.isFinite(thresholdMin) && thresholdMin >= 0
    ? thresholdMin
    : 15;
  if (currentShopMinutes < shiftStartShopMinutes + safeThreshold) {
    return 'Awaiting';
  }
  return 'No-Show Risk';
}
